import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import AccountHeroBit from '../../components/account/AccountHeroBit';
import ProfileCard from '../../components/account/ProfileCard';
import LuxuryButton from '../../components/ui/LuxuryButton';
import LuxuryLoading from '../../components/ui/LuxuryLoading';
import ChangePasswordModal from '../../components/account/ChangePasswordModal';
import NotificationsModal from '../../components/account/NotificationsModal';
import {
    FaBox, FaHeart, FaMapMarkerAlt, FaUser, FaSignOutAlt,
    FaShoppingBag, FaStar, FaCheckCircle, FaExclamationTriangle,
    FaCreditCard, FaTruck, FaSync, FaBell, FaClock
} from 'react-icons/fa';
import { cardFloat, cardStagger, pageTransition } from "../../components/animations/animations";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { AuthService } from "../../api/auth.api";
import { UserService } from "../../api/user.api";
import { OrderService } from "../../api/order.api";

const Dashboard = () => {
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(false);
    const [loading, setLoading] = useState({
        profile: true,
        stats: true,
        orders: true
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [user, setUser] = useState(null);
    const [stats, setStats] = useState(null);
    const [recentOrders, setRecentOrders] = useState([]);
    const [wishlistCount, setWishlistCount] = useState(0);

    // Modal states
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            setLoading({ profile: true, stats: true, orders: true });
            setError(null);

            const userData = await UserService.getCurrentUser();
            setUser(userData);

            const [ordersResponse, wishlistResponse] = await Promise.allSettled([
                OrderService.getMyOrders(),
                UserService.getWishlist()
            ]);

            const orders = ordersResponse.status === 'fulfilled' ? ordersResponse.value : [];
            const wishlist = wishlistResponse.status === 'fulfilled' ? wishlistResponse.value : [];

            const deliveredOrders = orders.filter(o => o.orderStatus === 'delivered').length;
            const processingOrders = orders.filter(o => o.orderStatus === 'processing' || o.orderStatus === 'confirmed').length;
            const cancelledOrders = orders.filter(o => o.orderStatus === 'cancelled').length;
            const totalSpent = orders.reduce((sum, order) => sum + (order.total || 0), 0);
            const rewardPoints = Math.floor(totalSpent / 10);

            setStats({
                totalOrders: orders.length,
                deliveredOrders,
                processingOrders,
                cancelledOrders,
                totalSpent,
                rewardPoints
            });

            setWishlistCount(wishlist.length || 0);
            setRecentOrders(orders.slice(0, 3));

            setSuccess(`Welcome back, ${userData.firstName}!`);
            setTimeout(() => setSuccess(null), 3000);

        } catch (err) {
            console.error('Dashboard load error:', err);
            setError(err.message || 'Failed to load dashboard data');

            const savedUser = localStorage.getItem('kromaic_user');
            if (savedUser) {
                setUser(JSON.parse(savedUser));
            } else {
                setTimeout(() => navigate('/login'), 3000);
            }
        } finally {
            setLoading({ profile: false, stats: false, orders: false });
        }
    };

    const handleLogout = async () => {
        try {
            await AuthService.logout();
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            localStorage.clear();
            window.location.href = '/login';
        }
    };

    const handleUpdateProfile = async (updatedData) => {
        try {
            setLoading(prev => ({ ...prev, profile: true }));

            const updatedUser = await UserService.updateProfile(updatedData);
            setUser(updatedUser);
            localStorage.setItem('kromaic_user', JSON.stringify(updatedUser));

            setSuccess('Profile updated successfully!');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to update profile');
        } finally {
            setLoading(prev => ({ ...prev, profile: false }));
        }
    };

    // Add this function for password change
    const handleChangePassword = async (passwordData) => {
        try {
            await UserService.changePassword(passwordData);
            setSuccess('Password changed successfully!');

            // Log out from all devices after password change
            setTimeout(() => {
                handleLogout();
            }, 2000);
        } catch (err) {
            throw new Error(err.message || 'Failed to change password');
        }
    };

    const getOrderStatusDisplay = (order) => {
        if (order.orderStatus) {
            return order.orderStatus;
        }
        if (order.paymentStatus === 'completed') {
            return 'confirmed';
        }
        if (order.paymentStatus === 'processing') {
            return 'processing';
        }
        return 'pending';
    };

    const getStatusColor = (status) => {
        if (!status || typeof status !== 'string') {
            return 'bg-nude/20 text-charcoal/70';
        }

        const normalizedStatus = status.toLowerCase();

        switch (normalizedStatus) {
            case 'delivered':
            case 'confirmed':
                return 'bg-sage/10 text-sage';
            case 'shipped':
                return 'bg-champagne/10 text-champagne';
            case 'processing':
            case 'pending':
                return 'bg-nude/20 text-charcoal/70';
            case 'cancelled':
                return 'bg-error/10 text-error';
            default:
                return 'bg-nude/20 text-charcoal/70';
        }
    };

    const getStatusText = (status) => {
        if (!status || typeof status !== 'string') {
            return 'Unknown';
        }
        const normalizedStatus = status.toLowerCase();

        switch (normalizedStatus) {
            case 'confirmed':
                return 'Confirmed';
            case 'processing':
                return 'Processing';
            case 'pending':
                return 'Pending';
            case 'shipped':
                return 'Shipped';
            case 'delivered':
                return 'Delivered';
            case 'cancelled':
                return 'Cancelled';
            default:
                return status.charAt(0).toUpperCase() + status.slice(1);
        }
    };

    // Update accountActions to use modals instead of navigation
    const accountActions = [
        {
            title: 'Manage Subscriptions',
            description: 'View and edit your subscriptions',
            action: () => navigate('/account/subscriptions'),
            icon: FaSync
        },
        {
            title: 'Change Password',
            description: 'Update your account password',
            action: () => setShowChangePassword(true),
            icon: FaUser
        },
        {
            title: 'Notifications',
            description: 'Configure your notification preferences',
            action: () => setShowNotifications(true),
            icon: FaBell
        },
        {
            title: 'Shipping Preferences',
            description: 'Set your delivery preferences',
            action: () => navigate('/account/shipping'),
            icon: FaTruck
        }
    ];

    const quickLinks = [
        {
            title: 'Order History',
            description: 'View and track your orders',
            icon: FaShoppingBag,
            link: '/account/orders',
            badge: stats?.totalOrders || 0
        },
        {
            title: 'Wishlist',
            description: 'Your saved products',
            icon: FaHeart,
            link: '/account/wishlist',
            badge: wishlistCount
        },
        {
            title: 'Address Book',
            description: 'Manage delivery addresses',
            icon: FaMapMarkerAlt,
            link: '/account/addresses'
        },
        {
            title: 'Payment Methods',
            description: 'Manage your payment options',
            icon: FaCreditCard,
            link: '/account/payments'
        }
    ];

    if (loading.profile && !user) {
        return <LuxuryLoading message="Preparing your luxury experience..." />;
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-8 luxury-card">
                    <div className="w-20 h-20 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FaExclamationTriangle className="w-10 h-10 text-error/60" />
                    </div>
                    <h2 className="text-2xl font-medium text-charcoal mb-2">Session Required</h2>
                    <p className="text-charcoal/60 mb-6">{error || 'Please login to access your dashboard'}</p>
                    <div className="space-y-3">
                        <LuxuryButton onClick={() => navigate('/login')} fullWidth>
                            Go to Login
                        </LuxuryButton>
                        <LuxuryButton variant="outline" onClick={() => navigate('/')} fullWidth>
                            Return Home
                        </LuxuryButton>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 overflow-x-hidden">
            {/* Navbar */}
            <div
                className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-soft' : 'bg-transparent'}`}
            >
                <Navbar user={user} />
            </div>

            <motion.div
                variants={pageTransition}
                initial="initial"
                animate="animate"
                exit="exit"
                className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 py-12 pt-28 pb-16"
            >
                <div className="container-luxury max-w-6xl mx-auto px-4">
                    {/* Notifications */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="mb-6 p-4 bg-error/10 border border-error/20 rounded-2xl flex items-center justify-between"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-error/20 flex items-center justify-center">
                                        <FaExclamationTriangle className="w-4 h-4 text-error" />
                                    </div>
                                    <p className="text-error text-sm">{error}</p>
                                </div>
                                <button
                                    onClick={() => setError(null)}
                                    className="text-charcoal/50 hover:text-charcoal"
                                >
                                    ✕
                                </button>
                            </motion.div>
                        )}

                        {success && (
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="mb-6 p-4 bg-sage/10 border border-sage/20 rounded-2xl flex items-center justify-between"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center">
                                        <FaCheckCircle className="w-4 h-4 text-sage" />
                                    </div>
                                    <p className="text-sage text-sm">{success}</p>
                                </div>
                                <button
                                    onClick={() => setSuccess(null)}
                                    className="text-charcoal/50 hover:text-charcoal"
                                >
                                    ✕
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Hero Welcome */}
                    <AccountHeroBit
                        title={`Hello, ${user.firstName}`}
                        subtitle="Welcome back to your personalized beauty journey"
                        background="default"
                    >
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-charcoal/60"
                        >
                            {user.email} • Member since {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long'
                        }) : 'Recently'}
                        </motion.p>
                    </AccountHeroBit>

                    {/* Stats Grid */}
                    <motion.div
                        variants={cardStagger}
                        initial="initial"
                        animate="animate"
                        className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
                    >
                        <motion.div
                            variants={cardFloat}
                            whileHover={{ y: -4 }}
                            className="luxury-card p-6"
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full bg-sage/10 text-sage flex items-center justify-center`}>
                                    <FaBox className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-2xl font-light text-charcoal">
                                        {loading.stats ? '...' : stats?.totalOrders || 0}
                                    </p>
                                    <p className="text-sm text-charcoal/50">Total Orders</p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            variants={cardFloat}
                            whileHover={{ y: -4 }}
                            className="luxury-card p-6"
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full bg-sage/10 text-sage flex items-center justify-center`}>
                                    <FaCheckCircle className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-2xl font-light text-charcoal">
                                        {loading.stats ? '...' : stats?.deliveredOrders || 0}
                                    </p>
                                    <p className="text-sm text-charcoal/50">Delivered</p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            variants={cardFloat}
                            whileHover={{ y: -4 }}
                            className="luxury-card p-6"
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full bg-champagne/10 text-champagne flex items-center justify-center`}>
                                    <FaClock className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-2xl font-light text-charcoal">
                                        {loading.stats ? '...' : stats?.processingOrders || 0}
                                    </p>
                                    <p className="text-sm text-charcoal/50">Processing</p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            variants={cardFloat}
                            whileHover={{ y: -4 }}
                            className="luxury-card p-6"
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full bg-champagne/10 text-champagne flex items-center justify-center`}>
                                    <FaCreditCard className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-2xl font-light text-charcoal">
                                        ${loading.stats ? '...' : (stats?.totalSpent || 0).toFixed(2)}
                                    </p>
                                    <p className="text-sm text-charcoal/50">Total Spent</p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>

                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Left Column: Profile & Quick Links */}
                        <div className="lg:col-span-2 space-y-8">
                            <ProfileCard
                                user={user}
                                onUpdate={handleUpdateProfile}
                                loading={loading.profile}
                            />

                            {/* Quick Links */}
                            <motion.div
                                variants={cardFloat}
                                className="luxury-card p-6"
                            >
                                <h3 className="font-heading text-xl font-medium text-charcoal mb-6">
                                    Quick Access
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {quickLinks.map((link, index) => (
                                        <motion.div
                                            key={link.title}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            whileHover={{ x: 4 }}
                                        >
                                            <Link
                                                to={link.link}
                                                className="flex items-center gap-4 p-4 rounded-xl hover:bg-nude/10 transition-colors group relative"
                                            >
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                                                    <link.icon className="w-4 h-4 text-champagne" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="font-medium text-charcoal group-hover:text-champagne transition-colors">
                                                            {link.title}
                                                        </h4>
                                                        {link.badge > 0 && (
                                                            <span className="text-xs px-2 py-1 bg-champagne/20 text-champagne rounded-full">
                                                                {link.badge}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-charcoal/50">{link.description}</p>
                                                </div>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>

                            {/* Account Actions */}
                            <motion.div
                                variants={cardFloat}
                                className="luxury-card p-6"
                            >
                                <h3 className="font-heading text-xl font-medium text-charcoal mb-6">
                                    Account Management
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {accountActions.map((action, index) => (
                                        <motion.button
                                            key={action.title}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            whileHover={{ x: 4 }}
                                            onClick={action.action}
                                            className="flex items-center gap-4 p-4 rounded-xl hover:bg-nude/10 transition-colors group text-left"
                                        >
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                                                <action.icon className="w-4 h-4 text-champagne" />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-medium text-charcoal group-hover:text-champagne transition-colors">
                                                    {action.title}
                                                </h4>
                                                <p className="text-sm text-charcoal/50">{action.description}</p>
                                            </div>
                                        </motion.button>
                                    ))}
                                </div>
                            </motion.div>
                        </div>

                        {/* Right Column: Recent Orders & Account Actions */}
                        <div className="space-y-8">
                            {/* Recent Orders */}
                            <motion.div
                                variants={cardFloat}
                                className="luxury-card p-6"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="font-heading text-xl font-medium text-charcoal">
                                        Recent Orders
                                    </h3>
                                    <Link
                                        to="/account/orders"
                                        className="text-sm text-champagne hover:text-champagne/80 transition-colors"
                                    >
                                        View all
                                    </Link>
                                </div>

                                {loading.orders ? (
                                    <div className="space-y-4">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="animate-pulse flex items-center justify-between p-4">
                                                <div className="space-y-2">
                                                    <div className="h-4 bg-nude/20 rounded w-24"></div>
                                                    <div className="h-3 bg-nude/20 rounded w-16"></div>
                                                </div>
                                                <div className="text-right space-y-2">
                                                    <div className="h-4 bg-nude/20 rounded w-20 ml-auto"></div>
                                                    <div className="h-3 bg-nude/20 rounded w-12 ml-auto"></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : recentOrders.length > 0 ? (
                                    <div className="space-y-4">
                                        {recentOrders.map((order, index) => {
                                            const status = getOrderStatusDisplay(order);
                                            return (
                                                <motion.div
                                                    key={order._id}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: index * 0.1 }}
                                                    className="flex items-center justify-between p-4 rounded-lg hover:bg-nude/5 transition-colors group"
                                                >
                                                    <div>
                                                        <p className="font-medium text-charcoal group-hover:text-champagne transition-colors">
                                                            {order.orderNumber || `#${order._id?.slice(-8) || 'N/A'}`}
                                                        </p>
                                                        <p className="text-sm text-charcoal/50">
                                                            {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : 'Date unavailable'}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-medium text-charcoal">
                                                            ${(order.total || 0).toFixed(2)}
                                                        </p>
                                                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(status)}`}>
                                                            {getStatusText(status)}
                                                        </span>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="text-charcoal/50">No orders yet</p>
                                        <Link
                                            to="/shop"
                                            className="text-sm text-champagne hover:text-champagne/80 transition-colors"
                                        >
                                            Start Shopping →
                                        </Link>
                                    </div>
                                )}
                            </motion.div>

                            {/* Membership Status */}
                            <motion.div
                                variants={cardFloat}
                                className="luxury-card p-6 bg-gradient-to-br from-champagne/5 to-transparent"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-champagne to-champagne/80 flex items-center justify-center">
                                        <FaStar className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-charcoal">KROMAIC Member</h4>
                                        <p className="text-sm text-charcoal/50">
                                            {stats?.rewardPoints >= 1000 ? 'Premium Tier' :
                                                stats?.rewardPoints >= 500 ? 'Gold Tier' : 'Silver Tier'}
                                        </p>
                                    </div>
                                </div>
                                <p className="text-sm text-charcoal/60 mb-4">
                                    Enjoy exclusive benefits including free shipping, early access, and personalized consultations.
                                </p>
                                <div className="space-y-3">
                                    <LuxuryButton
                                        variant="outline"
                                        size="small"
                                        onClick={() => navigate('/account/benefits')}
                                        fullWidth
                                    >
                                        View All Benefits
                                    </LuxuryButton>
                                    <LuxuryButton
                                        variant="secondary"
                                        size="small"
                                        onClick={handleLogout}
                                        icon={FaSignOutAlt}
                                        iconPosition="left"
                                        fullWidth
                                    >
                                        Sign Out
                                    </LuxuryButton>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Footer */}
            <Footer />

            {/* Modals */}
            <ChangePasswordModal
                isOpen={showChangePassword}
                onClose={() => setShowChangePassword(false)}
                onSubmit={handleChangePassword}
            />

            <NotificationsModal
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                user={user}
            />
        </div>
    );
};

export default Dashboard;